import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation, Trans } from 'react-i18next';

import {
  ScaleButton,
  ScaleIconActionSuccess,
  ScaleIconContentLock,
  ScaleIconCommunicationEmail,
  ScaleIconAlertInformation,
  ScaleIconUserFileUser,
  ScaleIconContentTodoList,
  ScaleLogo,
  ScaleNotificationBanner,
  ScaleLink,
} from '@cloudorder/ui';

import { useAuth } from '@/shared/auth/AuthContext';
import { LocaleSwitcher } from '@/shared/i18n';

import styles from './LoginPage.module.css';

/**
 * Where to land once the sign-in completes.
 *
 * The value arrives in a query string, so it is whatever the last link said.
 * Only a path at this origin is allowed through: a leading `//` or `/\` is a
 * protocol-relative URL that would carry the operator to another site while
 * looking like a local path. The service applies the same rule to the copy it
 * receives — this one guards the client-side navigation below.
 */
function safeNext(raw: string | null): string {
  if (raw === null || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return '/';
  }
  return raw;
}

export function LoginPage(): ReactElement {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('login');
  const [loading, setLoading] = useState(false);

  /* Captured on the first render rather than read on every one.
     Where to land is decided by the link that brought the operator here, and
     the redirect below *replaces* the URL. A value re-derived from the current
     query string therefore becomes `/` the instant that redirect lands, which
     re-runs the effect and carries them off the page they actually asked for —
     a deep link that works for signed-out operators and silently does not for
     signed-in ones. */
  const next = useRef(safeNext(searchParams.get('next'))).current;

  /* The service redirects back to `/login?error=…` when a sign-in does not
     complete, so the banner is driven by the URL rather than by a caught
     exception — `login()` leaves the page, and a navigation cannot fail in a
     way this component is still around to catch.

     Dismissal remembers the code it dismissed rather than a boolean, so a
     later failure raises the banner again without an effect to reset it. */
  const errorCode = searchParams.get('error');
  const [dismissedCode, setDismissedCode] = useState<string | null>(null);
  const error =
    errorCode === null || errorCode === dismissedCode
      ? null
      : errorCode === 'sign_in_expired'
        ? t('errorExpired')
        : t('errorSignIn');

  const FEATURES = [
    {
      icon: <ScaleIconAlertInformation decorative size={18} />,
      label: t('features.mriExtraction'),
    },
    {
      icon: <ScaleIconUserFileUser decorative size={18} />,
      label: t('features.humanInLoop'),
    },
    {
      icon: <ScaleIconContentLock decorative size={18} />,
      label: t('features.clarificationWorkflow'),
    },
    {
      icon: <ScaleIconContentTodoList decorative size={18} />,
      label: t('features.orderWorkflows'),
    },
    {
      icon: <ScaleIconCommunicationEmail decorative size={18} />,
      label: t('features.emailIntegration'),
    },
    {
      icon: <ScaleIconActionSuccess decorative size={18} />,
      label: t('features.audit'),
    },
  ];

  /* Somebody who is already signed in has no business on this screen — they
     arrive here from a bookmark, or from the redirect at the end of a sign-in
     that the guard has since confirmed. `replace` so Back does not return
     them to a page that only bounces them forward again. */
  useEffect(() => {
    if (isAuthenticated) {
      void navigate(next, { replace: true });
    }
  }, [isAuthenticated, navigate, next]);

  /* Hands the browser to the service, which hands it to Entra ID. Nothing
     resolves and nothing throws: the next thing this tab renders is either the
     dashboard or this page again with `?error=`. `loading` therefore only ever
     turns on — it labels the moment between the click and the page leaving. */
  function handleSignIn(): void {
    setLoading(true);
    login(next);
  }

  return (
    <div className={styles.page}>
      {/* ── Locale toggle — top-right corner, visible before login ─────── */}
      <span className={styles.localeToggle}>
        <LocaleSwitcher />
      </span>

      {/* ── Left: branding & feature panel ──────────────────────────────── */}
      <aside className={styles.left} aria-label={t('regions.product')}>
        {/* Brand mark */}
        <div className={styles.brand}>
          <div className={styles.brandLogoWrap}>
            <ScaleLogo className={styles.brandLogo} variant="white" size={35} />
          </div>
          <div className={styles.brandText}>
            <span className={styles.brandName}>PIXIE</span>
            <span className={styles.brandSub}>CLOUD ORDER MANAGEMENT</span>
          </div>
        </div>

        <div className={styles.divider} aria-hidden="true" />

        <h1 className={styles.headline}>
          {t('headline')}
        </h1>

        <p className={styles.description}>
          {t('description')}
        </p>

        <ul className={styles.features} aria-label={t('regions.capabilities')}>
          {FEATURES.map(({ icon, label }) => (
            <li key={label} className={styles.feature}>
              <span className={styles.featureIcon} aria-hidden="true">
                {icon}
              </span>
              {label}
            </li>
          ))}
        </ul>
      </aside>

      {/* ── Right: login card ────────────────────────────────────────────── */}
      <main className={styles.right}>
        <div className={styles.card} role="main">
          {/* Logo badge */}
          <div className={styles.cardBadge}>
            <ScaleLogo className={styles.cardBadgeLogo} size={28} />
            <div className={styles.cardBadgeText}>
              <span className={styles.cardBadgeName}>PIXIE</span>
              <span className={styles.cardBadgeSub}>CLOUD ORDER MANAGEMENT</span>
            </div>
          </div>

          <h2 className={styles.welcomeHeading}>{t('welcomeHeading')}</h2>
          <p className={styles.welcomeSub}>
            {t('welcomeSub')}
          </p>

          {/* Error banner — visible only when the service sent us back here */}
          {error !== null && (
            <div className={styles.errorBanner}>
              <ScaleNotificationBanner
                variant="error"
                dismissible
                onScaleClose={() => setDismissedCode(errorCode)}
              >
                {error}
              </ScaleNotificationBanner>
            </div>
          )}

          {/* Sign-in button */}
          <div className={styles.signInBtn}>
            <ScaleButton variant="primary" disabled={loading} onClick={handleSignIn}>
              {/* Microsoft coloured-grid icon */}
              <span className={styles.msIcon} aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
              </span>
              {loading ? t('signingIn') : t('signIn')}
            </ScaleButton>
          </div>

          <p className={styles.legal}>
            {/* One sentence with the links inside it, not three fragments joined
                by a hard-coded `and`. The German sentence ends in `zu` — a
                separable prefix that belongs after the second link — so a
                fragment assembled around the links can only be right in
                English. */}
            <Trans
              ns="login"
              i18nKey="legal"
              components={{
                terms: <ScaleLink href="#" />,
                privacy: <ScaleLink href="#" />,
              }}
            />
          </p>
        </div>
      </main>
    </div>
  );
}
